import { DataProvider, Identifier } from 'react-admin';
import {
    ACCOUNT_CREATED,
    CONTACT_CREATED,
    CONTACT_NOTE_CREATED,
    QUOTE_CREATED,
    QUOTE_NOTE_CREATED,
} from '../../consts';
import {
    Activity,
    Account,
    Contact,
    ContactNote,
    Quote,
    QuoteNote,
} from '../../types';

// FIXME: Requires 5 large queries to get the latest activities.
// Replace with a server-side view or a custom API endpoint.
export async function getActivityLog(
    dataProvider: DataProvider,
    accountId?: Identifier,
    salesId?: Identifier
) {
    let accountFilter = {} as any;
    if (accountId) {
        accountFilter.id = accountId;
    } else if (salesId) {
        accountFilter['sales_id@in'] = `(${salesId})`;
    }

    let filter = {} as any;
    if (accountId) {
        filter.account_id = accountId;
    } else if (salesId) {
        filter['sales_id@in'] = `(${salesId})`;
    }

    const [newAccounts, newContactsAndNotes, newQuotesAndNotes] =
        await Promise.all([
            getNewAccounts(dataProvider, accountFilter),
            getNewContactsAndNotes(dataProvider, filter),
            getNewQuotesAndNotes(dataProvider, filter),
        ]);
    return (
        [...newAccounts, ...newContactsAndNotes, ...newQuotesAndNotes]
            // sort by date desc
            .sort((a, b) =>
                a.date && b.date ? a.date.localeCompare(b.date) * -1 : 0
            )
            // limit to 250 activities
            .slice(0, 250)
    );
}

const getNewAccounts = async (
    dataProvider: DataProvider,
    filter: any
): Promise<Activity[]> => {
    const { data: accounts } = await dataProvider.getList<Account>('accounts', {
        filter,
        pagination: { page: 1, perPage: 250 },
        sort: { field: 'created_at', order: 'DESC' },
    });
    return accounts.map(account => ({
        id: `account.${account.id}.created`,
        type: ACCOUNT_CREATED,
        account_id: account.id,
        account,
        sales_id: account.sales_id,
        date: account.created_at,
    }));
};

async function getNewContactsAndNotes(
    dataProvider: DataProvider,
    filter: any
): Promise<Activity[]> {
    const { data: contacts } = await dataProvider.getList<Contact>('contacts', {
        filter,
        pagination: { page: 1, perPage: 250 },
        sort: { field: 'first_seen', order: 'DESC' },
    });

    let recentContactNotesFilter = {} as any;
    if (filter.sales_id) {
        recentContactNotesFilter.sales_id = filter.sales_id;
    }
    if (filter.account_id) {
        // No account_id field in contactNote, filtering by related contacts instead.
        // This filter is only valid if a account has less than 250 contact.
        const contactIds = contacts.map(contact => contact.id).join(',');
        recentContactNotesFilter['contact_id@in'] = `(${contactIds})`;
    }

    const { data: contactNotes } = await dataProvider.getList<ContactNote>(
        'contactNotes',
        {
            filter: recentContactNotesFilter,
            pagination: { page: 1, perPage: 250 },
            sort: { field: 'date', order: 'DESC' },
        }
    );

    const newContacts = contacts.map(contact => ({
        id: `contact.${contact.id}.created`,
        type: CONTACT_CREATED,
        account_id: contact.account_id,
        sales_id: contact.sales_id,
        contact,
        date: contact.first_seen,
    }));

    const newContactNotes = contactNotes.map(contactNote => ({
        id: `contactNote.${contactNote.id}.created`,
        type: CONTACT_NOTE_CREATED,
        sales_id: contactNote.sales_id,
        contactNote,
        date: contactNote.date,
    }));

    return [...newContacts, ...newContactNotes] as Activity[];
}

async function getNewQuotesAndNotes(
    dataProvider: DataProvider,
    filter: any
): Promise<Activity[]> {
    const { data: quotes } = await dataProvider.getList<Quote>('quotes', {
        filter,
        pagination: { page: 1, perPage: 250 },
        sort: { field: 'created_at', order: 'DESC' },
    });

    let recentQuoteNotesFilter = {} as any;
    if (filter.sales_id) {
        recentQuoteNotesFilter.sales_id = filter.sales_id;
    }
    if (filter.account_id) {
        // No account_id field in quoteNote, filtering by related quotes instead.
        // This filter is only valid if a quote has less than 250 notes.
        const quoteIds = quotes.map(quote => quote.id).join(',');
        recentQuoteNotesFilter['quote_id@in'] = `(${quoteIds})`;
    }

    const { data: quoteNotes } = await dataProvider.getList<QuoteNote>(
        'quoteNotes',
        {
            filter: recentQuoteNotesFilter,
            pagination: { page: 1, perPage: 250 },
            sort: { field: 'date', order: 'DESC' },
        }
    );

    const newQuotes = quotes.map(quote => ({
        id: `quote.${quote.id}.created`,
        type: QUOTE_CREATED,
        account_id: quote.account_id,
        sales_id: quote.sales_id,
        quote,
        date: quote.created_at,
    }));

    const newQuoteNotes = quoteNotes.map(quoteNote => ({
        id: `quoteNote.${quoteNote.id}.created`,
        type: QUOTE_NOTE_CREATED,
        sales_id: quoteNote.sales_id,
        quoteNote,
        date: quoteNote.date,
    }));

    return [...newQuotes, ...newQuoteNotes] as Activity[];
}
